/**
 * PermissionMatrixEditor — data-driven RBAC permission grid.
 *
 * - Fetches the module/action catalog from /api/admin/permissions/catalog
 *   so adding a new module on the backend automatically appears in the UI.
 * - Fetches role presets from /api/admin/permissions/preset/:role when the
 *   parent role changes, so the matrix auto-fills with sensible defaults.
 * - Emits the full permissions_v2 object via onChange so the parent form
 *   submits it alongside other user fields.
 * - Visual cues: row-level "View only" / "Full" badges and per-row "All / None".
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Alert, AlertDescription } from './ui/alert';
import { Lock, Shield, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  export: 'Export',
  approve: 'Approve',
  reject: 'Reject',
  dispose: 'Dispose',
  assign: 'Assign',
  schedule: 'Schedule',
  maintenance: 'Maintenance',
  disable: 'Disable',
};

const PermissionMatrixEditor = ({ role, value, onChange, autoFillOnRoleChange = true }) => {
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [autoFilling, setAutoFilling] = useState(false);
  const [lastAppliedRole, setLastAppliedRole] = useState(null);

  // Load catalog once
  useEffect(() => {
    let cancel = false;
    setLoadingCatalog(true);
    axios.get(`${API_URL}/api/admin/permissions/catalog`)
      .then((r) => { if (!cancel) setCatalog(r.data?.catalog || []); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoadingCatalog(false); });
    return () => { cancel = true; };
  }, []);

  // When the parent role changes, auto-fill from preset (unless disabled or already applied)
  useEffect(() => {
    if (!role || !autoFillOnRoleChange) return;
    if (role === lastAppliedRole) return;
    let cancel = false;
    setAutoFilling(true);
    axios.get(`${API_URL}/api/admin/permissions/preset/${role}`)
      .then((r) => {
        if (cancel) return;
        const preset = r.data?.permissions_v2 || {};
        onChange?.(preset);
        setLastAppliedRole(role);
      })
      .catch(() => {})
      .finally(() => { if (!cancel) setAutoFilling(false); });
    return () => { cancel = true; };
    // intentional: re-run only when role changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, autoFillOnRoleChange]);

  const matrix = useMemo(() => value || {}, [value]);

  const toggle = (moduleKey, action) => {
    const next = { ...(matrix || {}) };
    next[moduleKey] = { ...(next[moduleKey] || {}) };
    next[moduleKey][action] = !next[moduleKey][action];
    onChange?.(next);
  };

  const setRow = (moduleKey, actions, all) => {
    const next = { ...(matrix || {}) };
    next[moduleKey] = {};
    actions.forEach((a) => { next[moduleKey][a] = !!all; });
    onChange?.(next);
  };

  const setAll = (all) => {
    const next = {};
    catalog.forEach((m) => {
      next[m.key] = {};
      m.actions.forEach((a) => { next[m.key][a] = !!all; });
    });
    onChange?.(next);
  };

  // Stats for the summary header
  const stats = useMemo(() => {
    let granted = 0, total = 0;
    for (const m of catalog) {
      const block = matrix[m.key] || {};
      for (const a of m.actions) {
        total += 1;
        if (block[a]) granted += 1;
      }
    }
    return { granted, total };
  }, [catalog, matrix]);

  // Locked editor for super_admin (always all-true, not editable)
  if (role === 'super_admin') {
    return (
      <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
        <Lock className="h-4 w-4 text-amber-600" />
        <AlertDescription>
          <strong>Super Admin</strong> has unrestricted access to all modules and actions.
          The permission matrix is not editable for this role.
        </AlertDescription>
      </Alert>
    );
  }

  if (loadingCatalog) {
    return <div className="space-y-2">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>;
  }

  return (
    <div className="space-y-3" data-testid="permission-matrix-editor">
      {/* Header with grand totals + bulk actions */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 p-3">
        <div className="flex items-center gap-2 text-sm">
          <Shield className="h-4 w-4 text-primary" />
          <span className="font-medium">Permission Matrix</span>
          <Badge variant="outline" data-testid="permission-stats">
            {stats.granted} / {stats.total} granted
          </Badge>
          {autoFilling && <span className="text-xs text-muted-foreground">(loading preset…)</span>}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setAll(true)} data-testid="perm-grant-all">
            Grant all
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setAll(false)} data-testid="perm-revoke-all">
            Revoke all
          </Button>
        </div>
      </div>

      <Alert className="py-2">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Picking a role auto-fills sensible defaults. You can then tick or untick individual boxes before saving.
        </AlertDescription>
      </Alert>

      {/* Grid */}
      <div className="rounded-md border overflow-hidden">
        <div className="max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Module</th>
                <th className="text-left px-3 py-2 font-medium w-44">Actions</th>
                <th className="text-right px-3 py-2 font-medium w-32">Row controls</th>
              </tr>
            </thead>
            <tbody>
              {catalog.map((m) => {
                const block = matrix[m.key] || {};
                const grantedCount = m.actions.filter((a) => block[a]).length;
                const allGranted = grantedCount === m.actions.length;
                const noneGranted = grantedCount === 0;
                return (
                  <tr key={m.key} className="border-t" data-testid={`perm-row-${m.key}`}>
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium">{m.label}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{m.key}</div>
                      {!noneGranted && (
                        <Badge
                          variant="outline"
                          className={`mt-1 text-[10px] ${
                            allGranted
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}
                        >
                          {allGranted ? 'Full access' : `${grantedCount}/${m.actions.length}`}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-3">
                        {m.actions.map((a) => (
                          <label
                            key={a}
                            className="flex items-center gap-1.5 text-xs cursor-pointer"
                            data-testid={`perm-cell-${m.key}-${a}`}
                          >
                            <Checkbox
                              checked={!!block[a]}
                              onCheckedChange={() => toggle(m.key, a)}
                            />
                            <span>{ACTION_LABELS[a] || a}</span>
                          </label>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px]"
                          onClick={() => setRow(m.key, m.actions, true)}
                          data-testid={`perm-row-all-${m.key}`}
                        >
                          All
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px]"
                          onClick={() => setRow(m.key, m.actions, false)}
                          data-testid={`perm-row-none-${m.key}`}
                        >
                          None
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PermissionMatrixEditor;
